export type PartitionResult<TPass, TFail> = [pass: TPass[], fail: TFail[]];

/**
 * Partition an array into two sets using the provided type predicate.
 *
 * If it looks like TS isn't inferring the predicates type correctly; try specifying the type inline.
 * Example:
 * ```ts
 * // Before
 * partition(arr, isNumber);
 * // After
 * partition(arr, (val: string | number): val is number => typeof val === 'number');
 * ```
 *
 * @param arr - The input array to partition
 * @param filter - The type predicate to be used as the filter function
 * @returns A tuple containing two arrays. The first (left) array will contain all items that matched (passed) the
 * predicate, the second (right) will contain all those that didn't.
 */
export function partition<T, T1 extends T>(
    arr: T[],
    filter: (item: T) => item is T1,
): [pass: T1[], fail: Array<Exclude<T, T1>>];
/**
 * Partition an array into two sets using the provided filter function.
 *
 * @param arr - The input array to partition
 * @param filter - The filter function to use
 * @returns A tuple containing two arrays. The first (left) array will contain all items that passed the filter, the
 * second (right) will contain all those that didn't.
 */
export function partition<T>(arr: T[], filter: (item: T) => boolean): [pass: T[], fail: T[]];
export function partition<T>(arr: T[], filter: (item: T) => boolean): [pass: T[], fail: T[]] {
    const pass: T[] = [];
    const fail: T[] = [];
    for (const elem of arr) {
        if (filter(elem)) {
            pass.push(elem);
        } else {
            fail.push(elem);
        }
    }
    return [pass, fail];
}
